import React, { useState, useEffect } from 'react';
import TutorialFixed, { type TutorialSection } from './TutorialFixed';

interface SimpleTutorialSectionProps {
  section: string;
  children?: React.ReactNode;
  onDimensionSelect?: (dimensionId: number) => void;
  onViewModeChange?: (mode: 'student' | 'skill') => void;
}

// Función global para manejar el tutorial
declare global {
  interface Window {
    tutorialManager?: {
      startTutorial: () => void;
      isActive: () => boolean;
      shouldOpenInSection: (section: string) => boolean;
    };
  }
}

const SimpleTutorialSection: React.FC<SimpleTutorialSectionProps> = ({ 
  section,
  children,
  onDimensionSelect,
  onViewModeChange
}) => {
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);

  // Verificar si el tutorial debe abrirse en esta sección
  useEffect(() => {
    const checkTutorialState = () => {
      const tutorialState = localStorage.getItem('tutorialState');
      if (tutorialState) {
        try {
          const parsed = JSON.parse(tutorialState);
          const shouldOpen = parsed.isActive && parsed.currentSection === section;
          
          if (shouldOpen && !isTutorialOpen) {
            console.log(`🚀 SimpleTutorialSection[${section}]: Auto-abriendo tutorial`);
            setIsTutorialOpen(true);
          } else if (!shouldOpen && isTutorialOpen) {
            console.log(`🚪 SimpleTutorialSection[${section}]: Cerrando tutorial`);
            setIsTutorialOpen(false);
          }
        } catch (error) {
          console.warn('Error cargando estado del tutorial:', error);
          localStorage.removeItem('tutorialState');
        }
      } else if (isTutorialOpen) {
        // Si no hay estado guardado pero el tutorial está abierto, cerrarlo
        setIsTutorialOpen(false);
      }
    };

    checkTutorialState();

    // Escuchar eventos de cambio de estado del tutorial
    const handleTutorialStateChanged = () => {
      console.log(`🔄 SimpleTutorialSection[${section}]: Estado del tutorial cambió`);
      checkTutorialState();
    };

    window.addEventListener('tutorialStateChanged', handleTutorialStateChanged);

    // Verificar periódicamente (con menor frecuencia)
    const interval = setInterval(checkTutorialState, 2000);
    
    return () => {
      window.removeEventListener('tutorialStateChanged', handleTutorialStateChanged);
      clearInterval(interval);
    };
  }, [section, isTutorialOpen]);

  // Manejar navegación entre secciones
  const handleSectionChange = (newSection: TutorialSection) => {
    // Este callback debe ser proporcionado por el componente padre (App.tsx)
    console.log('🧭 SimpleTutorialSection: Solicitando navegación a', newSection);
    
    // Buscar el callback de navegación del componente padre
    const navigationEvent = new CustomEvent('tutorialNavigationRequest', {
      detail: { section: newSection }
    });
    window.dispatchEvent(navigationEvent);
  };

  return (
    <>
      {children}
      
      {/* Tutorial */}
      {isTutorialOpen && (
        <TutorialFixed
          isOpen={isTutorialOpen}
          onClose={() => setIsTutorialOpen(false)}
          onComplete={() => {
            localStorage.setItem('tutorialCompleted', 'true');
            setIsTutorialOpen(false);
          }}
          currentSection={section as TutorialSection}
          onSectionChange={handleSectionChange}
          onDimensionSelect={onDimensionSelect}
          onViewModeChange={onViewModeChange}
        />
      )}
    </>
  );
};

export default SimpleTutorialSection;
